import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Mail } from "lucide-react";
import { SenderPicker, getSavedSender } from "@/components/dashboard/SenderPicker";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Preview-before-send window for overdue payment reminders.
 * Recipient is auto-populated from contacts (editable / manual entry when
 * unknown); the sender mailbox is selectable and remembered for next time;
 * the full email body renders in a preview frame. Original invoice PDFs are
 * attached automatically on send.
 */
export const RemindPreviewModal = ({ customer, onClose, onSent }) => {
  const [preview, setPreview] = useState(null); // null = loading
  const [to, setTo] = useState("");
  const [sender, setSender] = useState(getSavedSender);
  const [sending, setSending] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let live = true;
    api.get("/billing/remind-preview", { params: { customer } })
      .then((r) => { if (live) { setPreview(r.data); setTo(r.data.to || ""); } })
      .catch((e) => { if (live) setLoadError(e?.response?.data?.detail || "Could not build the preview"); });
    return () => { live = false; };
  }, [customer]);

  const canSend = !!sender && to.includes("@") && !sending;

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post("/billing/remind", { customer, to, sender });
      toast.success(`Reminder sent to ${r.data.to} — ${r.data.count} invoice${r.data.count === 1 ? "" : "s"}, ${money(r.data.total)}${r.data.attachments ? ` · ${r.data.attachments} PDF${r.data.attachments === 1 ? "" : "s"} attached` : ""}`);
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reminder");
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-3" data-testid="remind-preview-modal" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#1a3fd1]">Payment reminder</p>
            <h3 className="text-sm font-bold text-gray-900">{customer}</h3>
          </div>
          <button onClick={onClose} data-testid="remind-preview-close" className="rounded border border-gray-200 px-2 py-1 text-sm text-gray-500 hover:text-gray-800"><X className="h-4 w-4" /></button>
        </div>

        {loadError ? (
          <p className="px-4 py-8 text-center text-sm text-red-600" data-testid="remind-preview-error">{loadError}</p>
        ) : preview === null ? (
          <p className="px-4 py-8 text-center text-sm text-gray-400">Building preview…</p>
        ) : (
          <>
            <div className="space-y-3 overflow-y-auto px-4 py-3">
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-gray-500">From</label>
                <SenderPicker value={sender} onChange={setSender} testid="remind-sender" emptyTestid="remind-no-mailbox" />
              </div>
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-gray-500">To</label>
                <input
                  type="email"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  placeholder="Enter recipient email"
                  data-testid="remind-to"
                  className={`w-full rounded border px-2 py-1.5 text-xs text-gray-800 outline-none focus:border-[#1a3fd1] ${preview.to ? "border-gray-300 bg-white" : "border-amber-300 bg-amber-50"}`}
                />
                {!preview.to && (
                  <p className="mt-1 text-[10px] text-amber-600">No email on file for this customer — enter one manually.</p>
                )}
              </div>
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-gray-500">Subject</label>
                <p className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-xs text-gray-700" data-testid="remind-subject">{preview.subject}</p>
              </div>
              <div>
                <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  Preview · {preview.count} invoice{preview.count === 1 ? "" : "s"} · {money(preview.total)} · PDFs attached automatically
                </label>
                <iframe title="Reminder email preview" srcDoc={preview.html} sandbox="" data-testid="remind-preview-frame" className="h-72 w-full rounded border border-gray-200 bg-white" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
              <button onClick={onClose} className="rounded border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50" data-testid="remind-cancel">Cancel</button>
              <button onClick={send} disabled={!canSend} data-testid="remind-confirm" className="inline-flex items-center gap-1.5 rounded bg-[#1a3fd1] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#ffffff] hover:bg-[#1633b8] disabled:opacity-50">
                <Mail className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Confirm & send"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
